import { Extension, Mark, Node, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { ReactNodeViewRenderer } from '@tiptap/react';
import TemplateFieldView from '../components/editor/TemplateFieldView';
import {
  createFieldId,
  TEMPLATE_FIELD_KINDS,
  TEMPLATE_FIELD_SOURCES,
  unwrapFieldInstruction,
  wrapFieldInstruction,
} from '../utils/templateFields';

/**
 * Attribute specs shared by inline and block template fields (CONTRACTS §5).
 * The instruction is stored as an attribute and saved as the element's text content wrapped
 * in legacy braces (`{Instruction}`, `{?Instruction}` when optional) so generation pipelines
 * that only fill `{…}` placeholders keep working; the braces are removed again when parsing.
 * @param {'text'|'rich'} defaultKind - Default output kind for this field flavour.
 * @returns {object} TipTap attribute specs.
 */
const fieldAttributes = (defaultKind) => ({
  id: {
    default: null,
    // Fields without an id get one at parse time so it stays stable across serializations.
    parseHTML: (el) => el.getAttribute('data-tpl-field') || createFieldId(),
    renderHTML: (attrs) => ({ 'data-tpl-field': attrs.id || '' }),
  },
  kind: {
    default: defaultKind,
    parseHTML: (el) => {
      const kind = el.getAttribute('data-kind');
      return TEMPLATE_FIELD_KINDS.includes(kind) ? kind : defaultKind;
    },
    renderHTML: (attrs) => ({ 'data-kind': attrs.kind || defaultKind }),
  },
  required: {
    default: true,
    parseHTML: (el) => el.getAttribute('data-required') !== 'false',
    renderHTML: (attrs) => ({ 'data-required': attrs.required === false ? 'false' : 'true' }),
  },
  source: {
    default: 'any',
    parseHTML: (el) => {
      const source = el.getAttribute('data-source');
      return TEMPLATE_FIELD_SOURCES.includes(source) ? source : 'any';
    },
    renderHTML: (attrs) => ({ 'data-source': attrs.source || 'any' }),
  },
  maxWords: {
    default: null,
    parseHTML: (el) => {
      const value = parseInt(el.getAttribute('data-max-words'), 10);
      return Number.isFinite(value) && value > 0 ? value : null;
    },
    renderHTML: (attrs) => (attrs.maxWords ? { 'data-max-words': String(attrs.maxWords) } : {}),
  },
  instruction: {
    default: '',
    parseHTML: (el) => unwrapFieldInstruction((el.textContent || '').replace(/ /g, ' ').replace(/\s+/g, ' ').trim()),
    renderHTML: () => ({}),
  },
});

/**
 * Inline template field chip: `<span data-tpl-field …>{Instruction}</span>`.
 */
export const TemplateField = Node.create({
  name: 'templateField',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return fieldAttributes('text');
  },

  parseHTML() {
    return [{ tag: 'span[data-tpl-field]', priority: 100 }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes), wrapFieldInstruction(node.attrs.instruction, node.attrs.required)];
  },

  renderText({ node }) {
    return `[AI field: ${node.attrs.instruction || ''}]`;
  },

  addNodeView() {
    return ReactNodeViewRenderer(TemplateFieldView, { as: 'span' });
  },
});

/**
 * Block template field card: `<div data-tpl-field …>{Instruction}</div>`.
 */
export const TemplateFieldBlock = Node.create({
  name: 'templateFieldBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return fieldAttributes('rich');
  },

  parseHTML() {
    return [{ tag: 'div[data-tpl-field]', priority: 100 }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes), wrapFieldInstruction(node.attrs.instruction, node.attrs.required)];
  },

  renderText({ node }) {
    return `[AI field: ${node.attrs.instruction || ''}]`;
  },

  addNodeView() {
    return ReactNodeViewRenderer(TemplateFieldView);
  },
});

/** Shared attributes for generated-document field wrappers. */
const generatedFieldAttributes = () => ({
  fieldId: {
    default: null,
    parseHTML: (el) => el.getAttribute('data-field-id'),
    renderHTML: (attrs) => (attrs.fieldId ? { 'data-field-id': attrs.fieldId } : {}),
  },
  status: {
    default: null,
    parseHTML: (el) => el.getAttribute('data-field-status'),
    renderHTML: (attrs) => (attrs.status ? { 'data-field-status': attrs.status } : {}),
  },
  evidence: {
    default: null,
    parseHTML: (el) => el.getAttribute('data-evidence'),
    renderHTML: (attrs) => (attrs.evidence ? { 'data-evidence': attrs.evidence } : {}),
  },
});

/**
 * Block wrapper the worker emits around each filled block field in a generated document:
 * `<div data-field-id="f_x" data-field-status="filled|needs_input" data-evidence="R1,S2">…</div>`.
 * Kept through edits so the editor can offer "Regenerate section".
 */
export const GeneratedField = Node.create({
  name: 'generatedField',
  group: 'block',
  content: 'block+',
  defining: true,

  addAttributes() {
    return generatedFieldAttributes();
  },

  parseHTML() {
    return [{ tag: 'div[data-field-id]', priority: 100 }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { class: 'q-generated-field' }), 0];
  },
});

/**
 * Inline counterpart of {@link GeneratedField}: `<span data-field-id …>text</span>`.
 */
export const GeneratedFieldMark = Mark.create({
  name: 'generatedFieldMark',
  inclusive: false,
  excludes: '',
  priority: 1100,

  addAttributes() {
    return generatedFieldAttributes();
  },

  parseHTML() {
    return [{ tag: 'span[data-field-id]', priority: 100 }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { class: 'q-generated-field-inline' }), 0];
  },
});

/**
 * Where the toolbar "AI field" button puts a new field, from the cursor.
 *   - Inline when the author is clearly writing a sentence: text on both sides of the cursor,
 *     the cursor right after a space, a text selection, or inside a list item or table cell.
 *   - Otherwise a section (block) field: replacing an empty paragraph, before a textblock when
 *     the cursor is at its start, or after it (end of a heading or paragraph, empty heading).
 * @param {import('@tiptap/pm/state').EditorState} state - Editor state.
 * @returns {{where: 'inline'|'replace'|'before'|'after', before?: number, after?: number}} Placement and the textblock's bounds.
 */
export function fieldPlacement(state) {
  const { $from, empty } = state.selection;
  const parent = $from.parent;
  if (!parent.isTextblock) return { where: 'inline' };
  for (let d = $from.depth; d > 0; d -= 1) {
    const name = $from.node(d).type.name;
    if (name === 'listItem' || name === 'taskItem' || name === 'tableCell' || name === 'tableHeader') {
      return { where: 'inline' };
    }
  }
  if (!empty) return { where: 'inline' };
  const bounds = { before: $from.before($from.depth), after: $from.after($from.depth) };
  if (parent.content.size === 0) {
    return { where: parent.type.name === 'paragraph' ? 'replace' : 'after', ...bounds };
  }
  const textBefore = parent.textBetween(0, $from.parentOffset, '\n', '￼');
  const textAfter = parent.textBetween($from.parentOffset, parent.content.size, '\n', '￼');
  if (textBefore.length > 0 && textAfter.trim().length > 0) return { where: 'inline' };
  if (/\s$/.test(textBefore) && textBefore.trim().length > 0) return { where: 'inline' };
  if (textBefore.trim().length === 0) return { where: 'before', ...bounds };
  return { where: 'after', ...bounds };
}

/** Top-level node types that can be locked against AI edits. */
const LOCKABLE_TYPES = [
  'paragraph',
  'heading',
  'blockquote',
  'bulletList',
  'orderedList',
  'taskList',
  'codeBlock',
  'table',
  'image',
  'mermaidBlock',
  'trustSection',
  'templateFieldBlock',
  'generatedField',
];

/**
 * Template-only global attributes: `data-tpl-locked` on blocks and
 * `data-tpl-repeat` / `data-filter` on table rows, plus lock/field commands.
 */
export const TemplateAttributes = Extension.create({
  name: 'templateAttributes',

  addGlobalAttributes() {
    return [
      {
        types: LOCKABLE_TYPES,
        attributes: {
          tplLocked: {
            default: false,
            keepOnSplit: false,
            parseHTML: (el) => el.getAttribute('data-tpl-locked') === 'true',
            renderHTML: (attrs) => (attrs.tplLocked ? { 'data-tpl-locked': 'true' } : {}),
          },
        },
      },
      {
        types: ['tableRow'],
        attributes: {
          tplRepeat: {
            default: null,
            keepOnSplit: true,
            parseHTML: (el) => el.getAttribute('data-tpl-repeat'),
            renderHTML: (attrs) => (attrs.tplRepeat ? { 'data-tpl-repeat': attrs.tplRepeat } : {}),
          },
          tplFilter: {
            default: null,
            keepOnSplit: true,
            parseHTML: (el) => el.getAttribute('data-filter'),
            renderHTML: (attrs) => (attrs.tplFilter ? { 'data-filter': attrs.tplFilter } : {}),
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      /**
       * Toggle `data-tpl-locked` on the top-level block containing the selection.
       * @returns {boolean} Whether a block was toggled.
       */
      toggleBlockLock: () => ({ state, tr, dispatch }) => {
        const { $from } = state.selection;
        if ($from.depth < 1 && !state.selection.node) return false;
        const pos = $from.depth >= 1 ? $from.before(1) : state.selection.from;
        const node = state.doc.nodeAt(pos);
        if (!node || !Object.prototype.hasOwnProperty.call(node.attrs, 'tplLocked')) return false;
        if (dispatch) tr.setNodeAttribute(pos, 'tplLocked', !node.attrs.tplLocked);
        return true;
      },

      /**
       * Insert a template field. Without `block`, the cursor decides (see fieldPlacement): a
       * section (block) field unless the author is clearly writing a sentence, in which case an
       * inline field goes at the cursor. `block: true` / `block: false` force the flavour.
       * @param {{block?: boolean, instruction?: string, kind?: string, required?: boolean, source?: string, maxWords?: number|null}} [options] - Field attributes.
       * @returns {boolean} Whether a field was inserted.
       */
      insertTemplateField: (options = {}) => ({ state, chain, editor }) => {
        const id = createFieldId();
        const placement = fieldPlacement(state);
        let where = placement.where;
        if (options.block === false) where = 'inline';
        else if (options.block === true && where === 'inline') where = 'split';
        const asBlock = where !== 'inline';
        const attrs = {
          id,
          instruction: options.instruction || '',
          kind: options.kind || (asBlock ? 'rich' : 'text'),
          required: options.required !== false,
          source: options.source || 'any',
          maxWords: options.maxWords || null,
        };
        if (editor.storage.quillRuntime) editor.storage.quillRuntime.pendingEditFieldId = id;
        const block = { type: 'templateFieldBlock', attrs };
        if (where === 'replace') {
          return chain().insertContentAt({ from: placement.before, to: placement.after }, block).run();
        }
        if (where === 'after') return chain().insertContentAt(placement.after, block).run();
        if (where === 'before') return chain().insertContentAt(placement.before, block).run();
        // 'split' (forced block mid-text) and 'inline' insert at the cursor.
        return chain()
          .insertContent(asBlock ? block : { type: 'templateField', attrs })
          .run();
      },

      /**
       * Switch a field between a section (block) and an inline field, keeping its id,
       * instruction, source, required flag and max words. Output kind follows the flavour:
       * inline fields write plain text; a "Text" block becomes "Paragraphs".
       *   - inline → block: if the field is the only content of its paragraph, the paragraph
       *     becomes the block; otherwise the field moves to a block right after its paragraph.
       *   - block → inline: the block becomes a paragraph holding the inline field.
       * Fields shown in a pending review (diff) are not converted.
       * @param {string} fieldId - Field id.
       * @param {boolean} toBlock - True for a section field, false for inline.
       * @returns {boolean} Whether the field was found and converted (or already had that flavour).
       */
      convertTemplateField: (fieldId, toBlock) => ({ state, tr, dispatch }) => {
        let target = null;
        state.doc.descendants((node, pos) => {
          if (target) return false;
          if ((node.type.name === 'templateField' || node.type.name === 'templateFieldBlock') && node.attrs.id === fieldId) {
            target = { node, pos };
            return false;
          }
          return true;
        });
        if (!target) return false;
        const { node, pos } = target;
        const isBlock = node.type.name === 'templateFieldBlock';
        if (isBlock === Boolean(toBlock)) return true;
        if (node.attrs.diffBlock || node.attrs.diffChangeId) return false;
        const { schema } = state;
        const fieldAttrs = {
          id: node.attrs.id,
          instruction: node.attrs.instruction,
          required: node.attrs.required,
          source: node.attrs.source,
          maxWords: node.attrs.maxWords,
        };

        if (!toBlock) {
          const inline = schema.nodes.templateField.create({ ...fieldAttrs, kind: 'text' });
          if (dispatch) tr.replaceWith(pos, pos + node.nodeSize, schema.nodes.paragraph.create(null, inline));
          return true;
        }

        const blockNode = schema.nodes.templateFieldBlock.create({
          ...fieldAttrs,
          kind: node.attrs.kind && node.attrs.kind !== 'text' ? node.attrs.kind : 'rich',
        });
        const $pos = state.doc.resolve(pos);
        const parent = $pos.parent;
        const parentStart = $pos.before($pos.depth);
        const parentEnd = $pos.after($pos.depth);
        let onlyField = parent.type.name === 'paragraph';
        parent.forEach((child) => {
          if (child === node) return;
          if (child.isText && !child.text.trim()) return;
          if (child.type.name === 'hardBreak') return;
          onlyField = false;
        });
        const container = $pos.node($pos.depth - 1);
        const index = $pos.index($pos.depth - 1);
        if (onlyField) {
          if (!container.canReplaceWith(index, index + 1, schema.nodes.templateFieldBlock)) return false;
          if (dispatch) tr.replaceWith(parentStart, parentEnd, blockNode);
          return true;
        }
        if (!container.canReplaceWith(index + 1, index + 1, schema.nodes.templateFieldBlock)) return false;
        if (dispatch) {
          tr.delete(pos, pos + node.nodeSize);
          tr.insert(tr.mapping.map(parentEnd), blockNode);
        }
        return true;
      },

      /**
       * Update attributes of the template field with the given id.
       * @param {string} fieldId - Field id.
       * @param {object} attrs - Attributes to merge.
       * @returns {boolean} Whether the field was found.
       */
      updateTemplateField: (fieldId, attrs) => ({ state, tr, dispatch }) => {
        let found = false;
        state.doc.descendants((node, pos) => {
          if (found) return false;
          if ((node.type.name === 'templateField' || node.type.name === 'templateFieldBlock') && node.attrs.id === fieldId) {
            found = true;
            if (dispatch) tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...attrs });
            return false;
          }
          return true;
        });
        return found;
      },
    };
  },
});

const variablePluginKey = new PluginKey('quillTemplateVariables');
const VARIABLE_RE = /\{\{\s*[a-zA-Z_][\w.]*\s*\}\}/g;

/**
 * Build `{{variable}}` chip decorations for a document.
 * @param {import('@tiptap/pm/model').Node} doc - Document.
 * @returns {DecorationSet} Decorations.
 */
function buildVariableDecorations(doc) {
  const decorations = [];
  doc.descendants((node, pos) => {
    if (!node.isText || !node.text || node.text.indexOf('{{') === -1) return;
    if (node.marks.some((mark) => mark.type.name === 'code')) return;
    VARIABLE_RE.lastIndex = 0;
    let match = VARIABLE_RE.exec(node.text);
    while (match !== null) {
      const from = pos + match.index;
      const name = match[0].replace(/[{}\s]/g, '');
      decorations.push(Decoration.inline(from, from + match[0].length, {
        class: 'q-tpl-variable',
        'data-variable': name,
        title: `Variable: ${name} (filled automatically)`,
      }));
      match = VARIABLE_RE.exec(node.text);
    }
  });
  return DecorationSet.create(doc, decorations);
}

/**
 * Highlights `{{variable}}` tokens as chips. Variables stay plain text in the document so
 * the worker and exports see exactly what the author typed.
 */
export const TemplateVariableHighlight = Extension.create({
  name: 'templateVariableHighlight',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: variablePluginKey,
        state: {
          init: (_, state) => buildVariableDecorations(state.doc),
          apply: (tr, old) => (tr.docChanged ? buildVariableDecorations(tr.doc) : old),
        },
        props: {
          decorations(state) {
            return variablePluginKey.getState(state);
          },
        },
      }),
    ];
  },
});
